import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Navigate, Outlet, useNavigate } from 'react-router'

import { authApi, type CurrentUser } from '@/api/auth'
import { ApiError } from '@/api/client'
import { BrandLoader } from '@/components/feedback/BrandLoader'
import { AppShell } from '@/components/shell/AppShell'

/**
 * Guarda das rotas autenticadas.
 *
 * A sessão só existe no servidor: a única forma honesta de saber se ainda vale
 * é perguntar ao BFF. Um 401 aqui manda para o login — e como o cookie é
 * `HttpOnly`, não há estado no browser que possa mentir sobre isso.
 */
export function ProtectedLayout() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { data, isPending, isError, error } = useQuery<CurrentUser>({
    queryKey: ['auth', 'me'],
    queryFn: () => authApi.me(),
    retry: false,
    staleTime: 5 * 60 * 1000,
  })

  const [leaving, setLeaving] = useState(false)

  const logout = async () => {
    setLeaving(true)

    try {
      await authApi.logout()
    } finally {
      // Limpa tudo antes de sair: voltar no navegador não pode mostrar dados
      // do cache de quem acabou de sair (critério 6 da §1).
      queryClient.clear()
      void navigate('/login', { replace: true, viewTransition: true })
    }
  }

  if (isPending) return <BrandLoader />

  if (isError) {
    const expired = error instanceof ApiError && error.status === 401
    return <Navigate to={expired ? '/login?expired=1' : '/login'} replace />
  }

  return (
    <AppShell user={data} leaving={leaving} onLogout={() => void logout()}>
      <Outlet />
    </AppShell>
  )
}
